import { scaleLinear } from "d3-scale"
import { area } from "d3-shape"
import { createSelector } from "reselect"
import { calculateHighlighter } from "../helpers/highlighter"
import moment from "moment"

const MARGINS = { top: 0, left: 0, right: 40, bottom: 0 }
const MIN_Y = 0
const MAX_Y = 1
export const HEIGHT = 200

const getDate = props => props.date
const getData = props => props.data
const getWidth = props => props.width || 150
const getStartDate = props => props.startDate
const getEndDate = props => props.endDate

const getMinX = createSelector(
  [getStartDate],
  startDate => moment(startDate).toDate(),
)

const getMaxX = createSelector(
  [getEndDate, getStartDate],
  (endDate, startDate) => {
    if (endDate === startDate) {
      return moment(endDate).add(1, "days").toDate()
    }
    return moment(endDate).toDate()
  },
)

const getDotMaxX = createSelector(
  [getEndDate],
  endDate => moment(endDate).toDate(),
)

export const getDotScaleX = createSelector(
  [getMinX, getDotMaxX, getWidth],
  (minX, maxX, width) => scaleLinear()
    .domain([minX, maxX])
    .rangeRound([0, width - MARGINS.left - MARGINS.right]),
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth],
  (minX, maxX, width) => scaleLinear()
    .domain([minX, maxX])
    .rangeRound([0, width - MARGINS.left - MARGINS.right]),
)

export const getScaleY = createSelector(
  [],
  () => scaleLinear()
    .domain([MIN_Y, MAX_Y])
    .rangeRound([HEIGHT - MARGINS.top, MARGINS.bottom]),
)

export const getBackgroundRect = createSelector(
  [getScaleX, getScaleY, getMaxX],
  (scaleX, scaleY, maxX) => ({
    x: MARGINS.left,
    y: MARGINS.top,
    height: scaleY(MIN_Y),
    width: scaleX(maxX),
  }),
)

const getArea = createSelector(
  [getScaleX, getScaleY],
  (scaleX, scaleY) => area()
    .x(d => scaleX(d.x))
    .y0(d => scaleY(d.y0))
    .y1(d => scaleY(d.y1)),
)

export const getGraphLines = createSelector(
  [getArea, getData, getEndDate],
  (area, data) => {
    const result = {
      factorsPath: area(data.map(d => ({ x: moment(d.date), y0: d.value, y1: MAX_Y }))),
      idioPath: area(data.map(d => ({ x: moment(d.date), y0: MIN_Y, y1: d.value }))),
      transform: `translate(${MARGINS.left},${MARGINS.top - MARGINS.bottom})`,
    }

    // Simulate two dates to be able to draw the full graph with a single datapoint
    if (data && data.length === 1) {
      result.factorsPath = area([{ x: moment(data[0].date), y0: data[0].value, y1: MAX_Y }, { x: moment(data[0].date).add(1, "days"), y0: data[0].value, y1: MAX_Y }])
      result.idioPath = area([{ x: moment(data[0].date), y0: MIN_Y, y1: data[0].value }, { x: moment(data[0].date).add(1, "days"), y0: MIN_Y, y1: data[0].value }])
    }
    return result
  },
)

export const getHighlighter = createSelector(
  [getDate, getScaleX, getScaleY, getMinX, getMaxX, getData],
  (date, scaleX, scaleY, minX, maxX, data) => data && data.length > 1 ? calculateHighlighter(date, scaleX, scaleY, minX, maxX, MIN_Y, MAX_Y) : null,
)

const getMarkX = createSelector(
  [getScaleX, getMaxX],
  (scaleX, maxX) => scaleX(maxX) + 4,
)

export const getTopMark = createSelector(
  [getMarkX, getScaleY],
  (markX, scaleY) => ({
    x: markX,
    y: scaleY(MAX_Y),
  }),
)

export const getBottomMark = createSelector(
  [getMarkX, getScaleY],
  (markX, scaleY) => ({
    x: markX,
    y: scaleY(MIN_Y),
  }),
)
